import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { MailCheck, Send, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LaunchList = () => {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/launch-list`, {
                name: form.name.trim(),
                email: form.email.trim(),
                message: form.message.trim() || null,
            });
            toast.success("You're on the launch list!", {
                description: "We'll email you the moment SpiceJam jars land in Ontario.",
            });
            setForm({ name: "", email: "", message: "" });
        } catch (err) {
            toast.error("Something went wrong", {
                description: "Please try again in a moment.",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="launch-list" className="relative overflow-hidden bg-coal py-28 sm:py-36" data-testid="launch-list-section">
            <div className="absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-nutmeg/10 blur-[140px]" />
            <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: EASE }}
                >
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-bonnetglow">Join the launch list</p>
                    <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-parchment sm:text-5xl lg:text-6xl">
                        First taste. <span className="font-editorial font-medium normal-case italic text-nutmeg">First jars.</span>
                    </h2>
                    <p className="mt-8 max-w-md text-base leading-relaxed text-sand sm:text-lg">
                        Leave your name and email and you'll be first to know when the Signature Jerk and Island
                        Signature BBQ land in Ontario — plus first word on the Master Chef Guide cookbook.
                    </p>
                    <div className="mt-10 flex items-center gap-4 rounded-2xl border border-white/10 bg-soot/60 p-5">
                        <MailCheck className="h-6 w-6 shrink-0 text-thymeglow" />
                        <p className="text-sm leading-relaxed text-sand/80">
                            One email when we launch. No spam, no noise — just the good stuff.
                        </p>
                    </div>
                </motion.div>

                <motion.form
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 48 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
                    className="rounded-3xl border border-white/10 bg-soot p-8 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.9)] sm:p-10"
                    data-testid="launch-list-form"
                >
                    <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-sand/70" htmlFor="ll-name">
                        Name
                    </label>
                    <Input
                        id="ll-name"
                        required
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Your name"
                        className="mt-2 h-12 border-white/15 bg-obsidian/60 text-parchment placeholder:text-sand/40 focus-visible:ring-nutmeg"
                        data-testid="launch-list-input-name"
                    />
                    <label className="mt-6 block text-xs font-semibold uppercase tracking-[0.2em] text-sand/70" htmlFor="ll-email">
                        Email
                    </label>
                    <Input
                        id="ll-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@example.com"
                        className="mt-2 h-12 border-white/15 bg-obsidian/60 text-parchment placeholder:text-sand/40 focus-visible:ring-nutmeg"
                        data-testid="launch-list-input-email"
                    />
                    <label className="mt-6 block text-xs font-semibold uppercase tracking-[0.2em] text-sand/70" htmlFor="ll-message">
                        Message <span className="normal-case tracking-normal text-sand/40">(optional)</span>
                    </label>
                    <Textarea
                        id="ll-message"
                        value={form.message}
                        onChange={set("message")}
                        placeholder="Tell us what you'd slather it on first..."
                        rows={4}
                        className="mt-2 border-white/15 bg-obsidian/60 text-parchment placeholder:text-sand/40 focus-visible:ring-nutmeg"
                        data-testid="launch-list-input-message"
                    />
                    <button
                        type="submit"
                        disabled={sending}
                        className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-nutmeg px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-obsidian transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-nutmegwarm hover:shadow-[0_16px_40px_-12px_rgba(234,179,8,0.5)] disabled:cursor-not-allowed disabled:opacity-60"
                        data-testid="launch-list-submit-button"
                    >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {sending ? "Joining..." : "Count me in"}
                    </button>
                </motion.form>
            </div>
        </section>
    );
};

export default LaunchList;
